'use client';

import React from 'react';
import { motion } from 'framer-motion';
import useGetHeroImageByTag from '@/components/hooks/use-get-hero-image-by-tag';
import { Button } from '@/components/ui/button';
import { Image } from '@/components/ui/Image';
import { MotionLink } from '@/components/ui/Motion';
import { StaggeredText } from '@/components/ui/StaggeredText';
import { TypographyH2, TypographySmall } from '@/components/ui/typography';
import { cls } from '@/lib/utils';
import SectionBase from '../section-base';
import { wrapperElementMotionProps as wrapperElementMotionProperties } from './animations';
import {
  SPOTLIGHT_TITLE,
  SPOTLIGHT_PROJECTS,
  type SpotlightProject,
} from './consts';

const SECTION_ID = 'custom-home-spotlight';

function SpotlightCard({ project }: { project: SpotlightProject }) {
  const tag = `hero_projects_${project.name.replace(/\s+/g, '_')}`;
  const { data: heroImage } = useGetHeroImageByTag({ tag });
  const projectHref = `/projects/${project.name.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div className="flex flex-col gap-3">
      {/* Project image */}
      <MotionLink href={projectHref} className="block">
        <Image
          src={heroImage ?? ''}
          alt={`${project.name} custom home`}
          fill
          sizes="(max-width: 640px) 100vw, 33vw"
          className="rounded-lg object-cover"
          wrapperClassName="aspect-4/3 relative w-full h-auto"
          style={{ objectFit: 'cover', aspectRatio: '4/3' }}
        />
      </MotionLink>

      {/* Project info */}
      <div className="flex flex-col items-center gap-2 text-center">
        <TypographySmall className="uppercase tracking-wider font-semibold">
          {project.name}
        </TypographySmall>

        <span className="text-lg md:text-xl font-prata font-semibold">
          {project.sqft}
        </span>

        {/* Feature badges */}
        <div className="flex flex-wrap justify-center gap-2">
          {[project.beds, project.baths, project.garage].filter(Boolean).map(badge => (
            <span
              key={badge}
              className={cls`bg-primary text-primary-foreground text-xs px-3 py-1 rounded-sm font-medium`}
            >
              {badge}
            </span>
          ))}
        </div>

        {/* View floor plan link — only shown when project has a matching floor plan */}
        {project.floorPlanSlug && (
          <MotionLink
            href={`/floor-plans/${project.floorPlanSlug}`}
            className="text-sm text-muted-foreground underline hover:text-foreground transition-colors mt-1"
          >
            View floor plan
          </MotionLink>
        )}
      </div>
    </div>
  );
}

function OurSuccessSection() {
  return (
    <SectionBase
      id={SECTION_ID}
      containerProps={{ id: SECTION_ID }}
      background="bg-card"
    >
      <motion.div
        className={cls`flex flex-col items-center gap-10`}
        {...wrapperElementMotionProperties}
      >
        {/* Title */}
        <TypographyH2 className={cls`text-center border-b-0`}>
          <StaggeredText staggerChildren={0.03} delay={0.1}>
            {SPOTLIGHT_TITLE}
          </StaggeredText>
        </TypographyH2>

        {/* 3-column project cards (2 rows of 3) */}
        <div className={cls`grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-10 w-full`}>
          {SPOTLIGHT_PROJECTS.map(project => (
            <SpotlightCard key={project.name} project={project} />
          ))}
        </div>

        {/* CTA */}
        <Button asChild>
          <MotionLink href="/projects">View Our Projects</MotionLink>
        </Button>
      </motion.div>
    </SectionBase>
  );
}

export default OurSuccessSection;
